import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Loader2, ArrowUpCircle, CheckCircle2, XCircle, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const zar = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  pending:      'bg-yellow-100 text-yellow-800 border-yellow-200',
  acknowledged: 'bg-blue-100 text-blue-800 border-blue-200',
  matched:      'bg-green-100 text-green-800 border-green-200',
  rejected:     'bg-red-100 text-red-800 border-red-200',
};

export default function AdminContributionRequestsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      setLoading(true);
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const r = await axios.get(`${API_URL}/api/admin/contribution-requests${params}`, authHeader);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, token]);

  const viewProof = async (row) => {
    if (!row.has_proof_of_payment && !row.proof_of_payment_name) {
      toast.error('No proof of payment on file for this contribution');
      return;
    }
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/contribution-requests/${row.id}/proof-of-payment`,
        { ...authHeader, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' })
      );
      window.open(url, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open proof of payment');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-contributions-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1D2D2A] font-['Manrope']">Contributions / Additions</h1>
        <p className="text-[#6C757D] mt-1">Investors have flagged these incoming funds. Match each row against the actual bank inflow, then mark as <em>matched</em>.</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><ArrowUpCircle className="h-5 w-5 text-[#18A37C]" /> Flagged contributions</CardTitle>
            <CardDescription>{items.length} row{items.length === 1 ? '' : 's'}</CardDescription>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56" data-testid="admin-contrib-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="matched">Matched</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-[#6C757D]"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-[#6C757D]">No contribution requests match this filter.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date filed</TableHead>
                  <TableHead>Investor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Proof</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(r => (
                  <TableRow key={r.id} data-testid={`admin-contrib-row-${r.id}`}>
                    <TableCell className="text-xs">{new Date(r.created_at).toLocaleDateString('en-ZA')}</TableCell>
                    <TableCell>{r.investor_name}<div className="text-xs text-[#6C757D]">{r.investor_email}</div></TableCell>
                    <TableCell className="text-right font-mono">{zar(r.amount)}</TableCell>
                    <TableCell className="text-xs">{r.expected_date || '—'}</TableCell>
                    <TableCell className="text-xs">{r.payment_reference || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {r.has_proof_of_payment ? (
                        <button
                          type="button"
                          onClick={() => viewProof(r)}
                          className="text-[#18A37C] hover:underline inline-flex items-center gap-1"
                          data-testid={`admin-contrib-view-pop-${r.id}`}
                          title="Open proof of payment"
                        >
                          <Eye className="h-3 w-3" /> {r.proof_of_payment_name || 'View'}
                        </button>
                      ) : (
                        <span className="text-[#6C757D]">
                          {r.proof_of_payment_name ? `${r.proof_of_payment_name} (not uploaded)` : '—'}
                        </span>
                      )}
                    </TableCell>
                    <TableCell><Badge className={`${STATUS_CLS[r.status] || STATUS_CLS.pending} border`}>{r.status}</Badge></TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(r)} data-testid={`admin-contrib-edit-${r.id}`}>Review</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ReviewDialog editing={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={() => { setEditing(null); load(); }} authHeader={authHeader} />
    </div>
  );
}

function ReviewDialog({ editing, onOpenChange, onSaved, authHeader }) {
  const [status, setStatus] = useState('pending');
  const [notes, setNotes] = useState('');
  const [matchId, setMatchId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setStatus(editing.status || 'pending');
      setNotes(editing.admin_notes || '');
      setMatchId(editing.matched_transaction_id || '');
    }
  }, [editing]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/contribution-requests/${editing.id}`, {
        status,
        admin_notes: notes || null,
        matched_transaction_id: matchId || null,
      }, authHeader);
      const statusChanged = status !== (editing.status || 'pending');
      toast.success(
        statusChanged
          ? `Contribution updated — investor notified by email.`
          : 'Contribution updated.'
      );
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Review contribution</DialogTitle>
          <DialogDescription>
            {editing && (<>
              {editing.investor_name} · <span className="font-mono">{zar(editing.amount)}</span> · Filed {new Date(editing.created_at).toLocaleDateString('en-ZA')}
            </>)}
          </DialogDescription>
        </DialogHeader>
        {editing && (
          <div className="space-y-4 py-2">
            {editing.notes && (
              <div className="bg-slate-50 border rounded-md p-3 text-sm text-[#212529]"><strong>Investor note:</strong> {editing.notes}</div>
            )}
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="admin-contrib-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged (waiting for funds)</SelectItem>
                  <SelectItem value="matched">Matched (funds received)</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Matched transaction ID (optional)</label>
              <Input data-testid="admin-contrib-matchid" value={matchId} onChange={e => setMatchId(e.target.value)} placeholder="investor_transactions.id" />
            </div>
            <div>
              <label className="text-sm font-medium">Admin notes</label>
              <Textarea data-testid="admin-contrib-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional message to file / investor" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-contrib-save" className="bg-[#18A37C] hover:bg-[#1D2D2A] text-white">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
             : status === 'matched' ? <><CheckCircle2 className="h-4 w-4 mr-2" /> Save (mark matched)</>
             : status === 'rejected' ? <><XCircle className="h-4 w-4 mr-2" /> Save (reject)</>
             : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
